import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Chip, CircularProgress, Drawer, Paper, Stack, SxProps, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material'
import { ExtraSmallButton, NonInteractiveTooltip } from '../custom'
import { mergeSideBarBottomButtonSx } from '../sideBarRight/sideBarBottomIconButtonSx'
import {
    createOverlayDrawerOnClose,
    sideBarOverlayDrawerPaperProps,
    sideBarOverlayDrawerProps,
    useSideBarOverlayDrawerEscape
} from '../sideBarRight/sideBarOverlayDrawerProps'
import CalendarIcon from '../../assets/icons/CalendarIcon'
import { CompanyEventsAndActions } from '../../proto/generated/calendar-event'
import { CompanyEventProto, PurposeType } from '../../proto/generated/company_event'
import { CorporateAction, Type } from '../../proto/generated/corporate-action'
import { useSelector } from 'react-redux'
import { selectCurrentTheme } from '../../selectors/common'
import ClearIcon from '../../assets/icons/ClearIcon'
import WeekPicker from '../date/WeekPicker'
import dayjs, { Dayjs } from 'dayjs'
import { getCalendarEvents } from '../../services/apis/calendars'
import EmptySearch from '../../assets/icons/EmptySearch'
import { getSecurityKey } from '../../util/security'
import SymbolTableCell from './SymbolTableCell'
import { Exchange } from '../../proto/generated/common'
import { generatePath } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import { getEndOfWeek, getStartOfWeek } from '../../util/general'
import PurposeTableCell from './PurposeTableCell'

type FilterCategory = 'Board Meeting' | 'Earnings' | 'Dividend' | 'Bonus' | 'Split' | 'Rights' | 'Others'

const displayedFilters: FilterCategory[] = ['Board Meeting', 'Earnings', 'Dividend', 'Bonus', 'Split', 'Rights', 'Others']

const categoryMap: Record<Type, FilterCategory> = {
    [Type.BONUS]: 'Bonus',
    [Type.SPLIT]: 'Split',
    [Type.RIGHTS]: 'Rights',
    [Type.INTERIM_DIVIDEND]: 'Dividend',
    [Type.FINAL_DIVIDEND]: 'Dividend',
    [Type.SPECIAL_DIVIDEND]: 'Dividend',
    [Type.TYPE_UNKNOWN]: 'Others',
    [Type.UNRECOGNIZED]: 'Others'
}

const dateCellSx: SxProps = {
    padding: '8px'
}

const tableCellProps: SxProps = {
    padding: '8px'
}

const tableRowProps: SxProps = {
    '&:hover': {
        backgroundColor: (theme: any) => theme.palette.action.hover
    }
}

const typeLabels: Record<Type, string> = {
    [Type.TYPE_UNKNOWN]: 'Unknown',
    [Type.BONUS]: 'Bonus',
    [Type.SPLIT]: 'Split',
    [Type.RIGHTS]: 'Rights',
    [Type.INTERIM_DIVIDEND]: 'Interim',
    [Type.FINAL_DIVIDEND]: 'Final',
    [Type.SPECIAL_DIVIDEND]: 'Special',
    [Type.UNRECOGNIZED]: 'Unrecognized'
}

export function Calendars() {
    const [open, setOpen] = useState(false)
    const [data, setData] = useState<{ [key: string]: CompanyEventsAndActions }>()
    const [loading, setLoading] = useState(true)
    const loadingRef = useRef(false)
    const darkTheme = useSelector(selectCurrentTheme)
    const [weekRange, setWeekRange] = useState<{ start: Dayjs; end: Dayjs } | null>(null)
    const [selectedFilter, setSelectedFilter] = useState<FilterCategory>('Board Meeting')
    const [isCurrentWeek, setIsCurrentWeek] = useState<boolean>(false)
    const tableRef = useRef<HTMLDivElement | null>(null)

    const setCurrentWeekRange = () => {
        const today = dayjs()
        const startOfCurrentWeek = dayjs(getStartOfWeek(today.toDate()))
        const endOfCurrentWeek = dayjs(getEndOfWeek(today.toDate()))
        setWeekRange({ start: startOfCurrentWeek, end: endOfCurrentWeek })
    }

    useEffect(() => {
        if (open) {
            setCurrentWeekRange()
            loadingRef.current = true
            if (tableRef.current) {
                tableRef.current.style.opacity = '0'
            }
            scrollToCurrentWeekIfNeeded(true)
        }
    }, [open])

    useEffect(() => {
        if (!open || !weekRange) return
        setLoading(true)
        loadingRef.current = true
        const fromDate = weekRange.start.format('YYYY-MM-DD')
        const toDate = weekRange.end.format('YYYY-MM-DD')
        getCalendarEvents({ from_date: fromDate, to_date: toDate })
            .then((response) => {
                const newdata: { [key: string]: CompanyEventsAndActions } = {}
                response.calendarEvents.forEach((event) => {
                    newdata[event.date] = event
                })
                setData(newdata)
            })
            .catch((error) => console.error('Error fetching calendar events:', error))
            .finally(() => {
                setLoading(false)
                loadingRef.current = false
            })
    }, [open, weekRange])

    const toggleDrawer = useCallback((state: boolean) => {
        setOpen(state)
    }, [])

    const handleCalendarButtonClick = useCallback(() => {
        setOpen(prev => !prev)
    }, [])

    const handleDrawerClose = useCallback(() => {
        toggleDrawer(false)
    }, [toggleDrawer])

    useSideBarOverlayDrawerEscape(open, handleDrawerClose)

    const handleSymbolClick = useCallback((primaryCode: string, exchange: Exchange) => {
        if (!primaryCode) {
            return null
        }
        const origin = window.location.origin
        return window.open(origin + `${generatePath(ROUTES.symbol, { symbol: getSecurityKey(exchange, primaryCode) })}`, '_blank')
    }, [])

    const handleFilterChange = useCallback((filter: FilterCategory) => {
        setSelectedFilter(filter)
    }, [])

    const handleWeekChange = useCallback((startDate: Dayjs, endDate: Dayjs) => {
        setWeekRange({ start: startDate, end: endDate })
        setIsCurrentWeek(false)
    }, [])

    const scrollToCurrentWeekIfNeeded = (initialLoad = false) => {
        const today = dayjs()
        const startOfThisWeek = dayjs(getStartOfWeek(today.toDate()))
        const endOfThisWeek = dayjs(getEndOfWeek(today.toDate()))

        const isAlreadyCurrentWeek = weekRange?.start.isSame(startOfThisWeek, 'day') && weekRange?.end.isSame(endOfThisWeek, 'day')

        if (isAlreadyCurrentWeek) {
            let i = 0
            const intervalId = setInterval(() => {
                if (loadingRef.current) {
                    i = i + 1
                    if (i === 100) {
                        clearInterval(intervalId)
                        if (initialLoad && tableRef.current) {
                            tableRef.current.style.opacity = '1'
                        }
                    }
                } else {
                    clearInterval(intervalId)
                    const todayRow = document.getElementById('scrollable-date')
                    if (todayRow) {
                        todayRow.scrollIntoView(initialLoad ? { block: 'start' } : { behavior: 'smooth', block: 'start' })
                    }
                    if (initialLoad && tableRef.current) {
                        tableRef.current.style.opacity = '1'
                    }
                }
            }, 100)
            return
        }
        loadingRef.current = true
        setCurrentWeekRange()
        setIsCurrentWeek(true)
        let i = 0
        const intervalId = setInterval(() => {
            if (loadingRef.current) {
                i = i + 1
                if (i === 100) {
                    clearInterval(intervalId)
                }
            } else {
                clearInterval(intervalId)
                const el = document.getElementById('scrollable-date')
                el?.scrollIntoView({ block: 'start' })
            }
        }, 100)
    }

    const renderRows = (data: any, selectedFilter: FilterCategory) => {
        if (!data || Object.keys(data).length === 0) {
            return <EmptyRow />
        }
        const rows = []
        let hasRenderedEvents = false

        for (const [date, value] of Object.entries(data)) {
            const { companyEvents, corporateActions } = value as {
                companyEvents: CompanyEventProto[]
                corporateActions: CorporateAction[]
            }

            const merged = [
                ...companyEvents.map((data) => ({
                    type: 'event',
                    data
                })),
                ...corporateActions.map((data) => ({ type: 'action', data }))
            ]

            const filtered = merged.filter(({ type, data }) => {
                if (type === 'event') {
                    const event = data as CompanyEventProto
                    const details = event.details?.toLowerCase() || ''
                    if (details.includes('intimation') && event.purpose?.length === 1) {
                        return false;
                    }
                    if (selectedFilter === 'Earnings') {
                        return event.purpose?.includes(PurposeType.FINANCIAL_RESULTS);
                    }

                    if (selectedFilter === 'Board Meeting') {
                        const allowedPurposes = [
                            PurposeType.DIVIDEND,
                            PurposeType.BOARD_MEETING,
                            PurposeType.FUND_RAISING,
                            PurposeType.SPLIT,
                            PurposeType.BUYBACK,
                            PurposeType.BONUS,
                            PurposeType.OTHER_BUSINESS_MATTERS,
                            PurposeType.VOLUNTARY_DELISTING,
                        ]; return (
                            !event.purpose.includes(PurposeType.FINANCIAL_RESULTS) && event.purpose?.some(purpose => allowedPurposes.includes(purpose))
                        );
                    }
                    return false
                } else {
                    const action = data as CorporateAction
                    const mappedCategory = categoryMap[action.type]
                    return selectedFilter === mappedCategory
                }
            })

            if (filtered.length === 0) continue

            hasRenderedEvents = true

            const isEvent = filtered[0].type === 'event'
            const formattedDate = new Date(date).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
            })
            if (isEvent) {
                rows.push(
                    <TableRow
                        key={`${date}-event-header`}
                        id={date === dayjs().format('YYYY-MM-DD') ? 'scrollable-date' : undefined}
                        sx={{
                            backgroundColor: (theme) => theme.palette.background.default,
                            position: 'sticky',
                            top: 0
                        }}
                    >
                        <TableCell sx={{
                            ...dateCellSx, fontWeight: 500, width: 180,
                            maxWidth: 180,
                            minWidth: 180
                        }}>{formattedDate}</TableCell>
                        <TableCell sx={{ ...dateCellSx, fontSize: '11px' }}>PURPOSE</TableCell>
                    </TableRow>
                )

                rows.push(...filtered.map((entry, i) => renderEventRow(entry, date, i)))
            } else {
                const firstAction = filtered[0].data as CorporateAction
                rows.push(renderHeaderRowForActionType(firstAction.type, formattedDate))
                rows.push(...filtered.map((entry, i) => renderCorporateActionRow(entry.data as CorporateAction, date, i)))
            }
        }

        return hasRenderedEvents ? rows : <EmptyRow />
    }

    const renderHeaderRowForActionType = (type: Type, date: string) => {
        let headers: string[] = []

        switch (type) {
            case Type.BONUS:
                headers = [`${date}`, 'New FV', 'Bonus Ratio']
                break
            case Type.SPLIT:
                headers = [`${date}`, 'New FV', 'Split Ratio']
                break
            case Type.RIGHTS:
                headers = [`${date}`, 'New FV', 'Premium', 'Rights Ratio']
                break
            case Type.INTERIM_DIVIDEND:
            case Type.FINAL_DIVIDEND:
            case Type.SPECIAL_DIVIDEND:
                headers = [`${date}`, 'Type', 'Amount', 'Percentage']
                break
            default:
                headers = [`${date}`, 'Details']
        }

        return (
            <TableRow
                key={`${date}-header-${type}`}
                id={date === dayjs().format('YYYY-MM-DD') ? 'scrollable-date' : undefined}
                sx={{ backgroundColor: (theme) => theme.palette.background.default, position: 'sticky', top: 0 }}
            >
                {headers.map((header, idx) => (
                    <TableCell
                        key={idx}
                        sx={{
                            ...dateCellSx, width: idx === 0 ? 180 : 'auto',
                            maxWidth: idx === 0 ? 180 : 'auto',
                            minWidth: idx === 0 ? 180 : 'auto', fontWeight: idx === 0 ? 500 : 'normal', textAlign: header === 'Type' ? 'center' : idx === 0 ? 'left' : 'right', fontSize: idx === 0 ? undefined : '11px'
                        }}
                    >
                        {idx === 0 ? date : header.toUpperCase()}
                    </TableCell>
                ))}
            </TableRow>
        )
    }

    const renderPrefixOrSuffix = (isCurrency: boolean) => (
        <Box
            component="span"
            sx={{
                color: (theme) => theme.palette.text.disabled,
                fontSize: '10px',
                display: 'inline',
                mx: '4px'
            }}
        >
            {isCurrency ? '₹' : '%'}
        </Box>
    )

    const renderCorporateActionRow = (action: CorporateAction, date: string, index: number) => {
        const symbolCell = <SymbolTableCell symbol={action.primaryCode} onClick={() => handleSymbolClick(action.primaryCode, action.primaryExchange)} exchange={action.primaryExchange} />
        const formatFixed = (value: number) => value.toFixed(2)
        switch (action.type) {
            case Type.BONUS:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-bonus-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.newFaceValue / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {action.ratioNumerator}:{action.ratioDenominator}
                        </TableCell>
                    </TableRow>
                )
            case Type.SPLIT:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-split-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.newFaceValue / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {action.ratioNumerator}:{action.ratioDenominator}
                        </TableCell>
                    </TableRow>
                )
            case Type.RIGHTS:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-rights-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.newFaceValue / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.rightsPremium / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {action.ratioNumerator}:{action.ratioDenominator}
                        </TableCell>
                    </TableRow>
                )
            case Type.INTERIM_DIVIDEND:
            case Type.FINAL_DIVIDEND:
            case Type.SPECIAL_DIVIDEND:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-dividend-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'center' }}>{typeLabels[action.type]}</TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {renderPrefixOrSuffix(true)}
                            {formatFixed(action.dividendAmount / 100)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellProps, textAlign: 'right' }}>
                            {formatFixed(action.dividendPercentage)}
                            {renderPrefixOrSuffix(false)}
                        </TableCell>
                    </TableRow>
                )
            default:
                return (
                    <TableRow sx={{ ...tableRowProps }} key={`${date}-unknown-${index}`}>
                        {symbolCell}
                        <TableCell sx={{ ...tableCellProps, textAlign: 'center' }}>{typeLabels[action.type]}</TableCell>
                    </TableRow>
                )
        }
    }

    const renderEventRow = (entry: any, date: string, index: number) => {
        const { primaryCode, details, primaryExchange, displayPurpose } = entry.data
        return (
            <TableRow sx={{ ...tableRowProps }} key={`${date}-event-${index}`}>
                <SymbolTableCell symbol={primaryCode} onClick={() => handleSymbolClick(primaryCode, primaryExchange)} exchange={primaryExchange} />
                <PurposeTableCell details={details} displayPurpose={displayPurpose} />
            </TableRow>
        )
    }

    const EmptyRow = () => (
        <TableRow>
            <TableCell colSpan={3} align="center">
                <Stack gap={2} justifyContent="center" alignItems="center" mt={4}>
                    <EmptySearch />
                    <Typography variant="body1">No Events Available</Typography>
                </Stack>
            </TableCell>
        </TableRow>
    )

    return (
        <div>
            <NonInteractiveTooltip arrow placement="left" title="Calendars">
                <span>
                    <ExtraSmallButton
                        id="calendars-button"
                        size="small"
                        color="inherit"
                        onClick={handleCalendarButtonClick}
                        data-event="Calendars Clicked"
                        sx={mergeSideBarBottomButtonSx(theme => ({
                            backgroundColor: open ? theme.palette.action.selected : 'transparent'
                        }))}
                    >
                        <CalendarIcon />
                    </ExtraSmallButton>
                </span>
            </NonInteractiveTooltip>

            <Drawer
                anchor="right"
                open={open}
                onClose={createOverlayDrawerOnClose(handleDrawerClose)}
                {...sideBarOverlayDrawerProps}
                PaperProps={sideBarOverlayDrawerPaperProps}
            >
                <Paper sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Stack gap={2} sx={{ flexShrink: 0 }}>
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <NonInteractiveTooltip title={'Open today'} arrow placement={'top'}>
                                    <Button onClick={() => scrollToCurrentWeekIfNeeded()} variant="outlined" color="inherit" sx={{ p: 0, height: 34 }}>
                                        Today
                                    </Button>
                                </NonInteractiveTooltip>
                                <WeekPicker onChange={handleWeekChange} isCurrentWeek={isCurrentWeek} />
                            </Stack>
                            <ExtraSmallButton sx={{ height: '34px', width: '34px' }} onClick={() => toggleDrawer(false)} color="inherit">
                                <ClearIcon sx={{ fontSize: '24px' }} />
                            </ExtraSmallButton>
                        </Stack>

                        {/* Filters */}
                        <Stack direction="row" flexWrap="wrap" rowGap={1}>
                            {displayedFilters
                                .filter((value) => value !== 'Others')
                                .map((value) => (
                                    <Chip
                                        key={value}
                                        label={value}
                                        clickable
                                        sx={{ mr: 1, fontWeight: 500 }}
                                        onClick={() => handleFilterChange(value)}
                                        variant={selectedFilter === value ? 'filled' : 'outlined'}
                                    />
                                ))}
                        </Stack>
                    </Stack>

                    {/* Event Table */}
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    )}
                    <Box ref={tableRef} sx={{ flexGrow: 1, overflow: 'auto', mt: 2 }}>
                        {!loading && (
                            <TableContainer
                                sx={{
                                    maxHeight: '100%',
                                    overflowY: 'auto',
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' }
                                }}
                            >
                                <Table stickyHeader>
                                    <TableBody>{renderRows(data, selectedFilter)}</TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Drawer>
        </div>
    )
}
